import { Blockquote } from '@ui';
import { useTranslation } from 'react-i18next';
import MatchRow from './MatchRow';
import { PreviewRow, RowDecision } from './types';

interface Props {
    rows: PreviewRow[];
    decisions: Record<number, RowDecision>;
    onChange: (bookingId: number, decision: RowDecision) => void;
}

/**
 * Maps every booking id to the 1-based index of an earlier row that already
 * targets the same member, so duplicates can be highlighted.
 */
export const computeCollisions = (rows: PreviewRow[], decisions: Record<number, RowDecision>) => {
    const firstUse: Record<number, number> = {};
    const collisions: Record<number, number> = {};

    rows.forEach((row, index) => {
        const decision = decisions[row.booking_id];
        if (decision?.action !== 'merge' || !decision.member_id) return;

        if (firstUse[decision.member_id]) collisions[row.booking_id] = firstUse[decision.member_id];
        else firstUse[decision.member_id] = index + 1;
    });

    return collisions;
};

/** Preview table listing one row per selected booking, with the assignment control and duplicate-target warning. */
const MatchPreviewTable = ({ rows, decisions, onChange }: Props) => {
    // Provides translated strings for the table headers.
    const { t } = useTranslation('bookings');
    // Recomputes which rows target the same member as an earlier row.
    const collisions = computeCollisions(rows, decisions);

    // Nothing survived the backend's filtering (e.g. an explicitly picked cancelled booking): explain why
    // instead of rendering an empty table with headers only.
    if (rows.length === 0) {
        return <Blockquote color="warning">{t('booking.createMember.emptyPreview')}</Blockquote>;
    }

    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse">
                <thead>
                    <tr className="tw-text-left tw-text-sm tw-text-accents-6">
                        <th className="tw-p-2">{t('booking.createMember.columns.booking')}</th>
                        <th className="tw-p-2">{t('booking.createMember.columns.resolved')}</th>
                        <th className="tw-p-2">{t('booking.createMember.columns.assignment')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <MatchRow
                            key={row.booking_id}
                            row={row}
                            decision={decisions[row.booking_id] ?? { action: 'create', member_id: null }}
                            collisionWithRow={collisions[row.booking_id]}
                            onChange={(decision) => onChange(row.booking_id, decision)}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default MatchPreviewTable;
